import { BrowserAgent } from "@newrelic/browser-agent/loaders/browser-agent";
import { useEffect } from "react";
import { useHasFeature } from "ui-library/HasFeature";
import { useActiveAccount } from "utils/useActiveAccount";

/** the agent loader executes immediately on instantiation, so keep a module-level singleton */
let browserAgent: BrowserAgent | undefined;

/** reset the singleton between tests; not for production use */
export const resetBrowserAgentForTests = () => {
	browserAgent = undefined;
};

/** starts New Relic RUM once the user is signed in so it captures all signed-in routes */
const useSetupNewRelic = () => {
	const { _id, name, userId } = useActiveAccount();
	const hasNewRelicRum = useHasFeature("new_relic_rum");

	useEffect(() => {
		if (!hasNewRelicRum || !userId || browserAgent) return;

		const {
			NEW_RELIC_ACCOUNT_ID,
			NEW_RELIC_AGENT_ID,
			NEW_RELIC_APPLICATION_ID,
			NEW_RELIC_LICENSE_KEY,
			NEW_RELIC_TRUST_KEY,
			REACT_APP_BASE_API_URL,
		} = window.SAM._env_;

		/** environments without New Relic config (e.g. local) don't report RUM */
		if (
			!NEW_RELIC_ACCOUNT_ID ||
			!NEW_RELIC_AGENT_ID ||
			!NEW_RELIC_APPLICATION_ID ||
			!NEW_RELIC_LICENSE_KEY ||
			!NEW_RELIC_TRUST_KEY
		) {
			return;
		}

		browserAgent = new BrowserAgent({
			info: {
				applicationID: NEW_RELIC_APPLICATION_ID,
				beacon: "bam.nr-data.net",
				errorBeacon: "bam.nr-data.net",
				licenseKey: NEW_RELIC_LICENSE_KEY,
				sa: 1,
			},
			init: {
				ajax: {
					deny_list: ["bam.nr-data.net"],
				},
				distributed_tracing: {
					enabled: true,
					/** dashboard-api is cross-origin; without this, DT headers never leave the browser */
					allowed_origins: [REACT_APP_BASE_API_URL],
					cors_use_tracecontext_headers: true,
				},
				/** Rollbar already covers errors; console capture would ship library noise and possible PII */
				logging: { enabled: false },
				performance: {
					/** detail can carry arbitrary objects attached to marks/measures, so keep it off */
					capture_detail: false,
					capture_measures: true,
				},
			},
			loader_config: {
				accountID: NEW_RELIC_ACCOUNT_ID,
				agentID: NEW_RELIC_AGENT_ID,
				applicationID: NEW_RELIC_APPLICATION_ID,
				licenseKey: NEW_RELIC_LICENSE_KEY,
				trustKey: NEW_RELIC_TRUST_KEY,
			},
		});

		browserAgent.setUserId(userId);
		if (_id) browserAgent.setCustomAttribute("account_id", _id);
		if (name) browserAgent.setCustomAttribute("account_name", name);
	}, [_id, hasNewRelicRum, name, userId]);
};

export default useSetupNewRelic;
